import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useViewport } from '../hooks/useViewport';
import type { GooglePlayBillingPlugin } from '../types/google-play-billing';

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";

interface RemoveAdsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseSuccess: () => void;
  onContinue?: () => void;
  variant?: "menu" | "postAd";
}

function getGooglePlayBilling(): GooglePlayBillingPlugin | null {
  if (typeof window === "undefined") {
    return null;
  }
  const cap = (window as any).Capacitor;
  if (!cap) {
    return null;
  }
  if (cap.Plugins?.GooglePlayBilling) {
    return cap.Plugins.GooglePlayBilling as GooglePlayBillingPlugin;
  }
  if (cap.getPlugin) {
    try {
      return cap.getPlugin("GooglePlayBilling") as GooglePlayBillingPlugin;
    } catch {
      return null;
    }
  }
  return null;
}

export default function RemoveAdsModal({ 
  isOpen, 
  onClose,
  onPurchaseSuccess,
  onContinue,
  variant = "menu"
}: RemoveAdsModalProps) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobile = viewport.isMobile;
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  const isPostAd = variant === "postAd";
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRemoveAds = async () => {
    if (!isAndroid) {
      setError("Remove Ads is only available on Android");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const billing = getGooglePlayBilling();
      if (!billing) {
        throw new Error("Google Play Billing is not available");
      }

      // Launch purchase flow
      const purchase = await billing.purchaseProduct({
        productId: "remove_ads"
      });

      if (!purchase || !purchase.purchaseToken) {
        throw new Error("Purchase was cancelled or failed");
      }

      // Verify purchase with backend
      const verifyRes = await fetch(`${API_BASE}/store/verify-google-play`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: "remove_ads",
          purchase_token: purchase.purchaseToken,
          package_name: "ai.goldrush.app",
          order_id: purchase.orderId || undefined
        }),
      });

      if (!verifyRes.ok) {
        const errorText = await verifyRes.text();
        throw new Error(errorText || "Purchase verification failed");
      }

      const verifyData = await verifyRes.json();
      if (!verifyData.ok) {
        throw new Error(verifyData.message || "Purchase verification failed");
      }

      // Purchase successful - refresh account and close/continue
      window.dispatchEvent(new CustomEvent('accountNeedsRefresh'));
      onPurchaseSuccess();
      if (isPostAd && onContinue) {
        onContinue();
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(err.message || "Failed to purchase Remove Ads");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ 
            background: 'rgba(0, 0, 0, 0.7)', 
            paddingTop: isAndroid && isMobile ? 'calc(env(safe-area-inset-top, 0px) + 1rem)' : (isMobile ? '1rem' : '10vh'),
            paddingBottom: isAndroid && isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' : (isMobile ? '1rem' : '1rem'),
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              if (isPostAd && onContinue) {
                onContinue();
              } else {
                onClose();
              }
            }
          }}
        >
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative w-full max-w-md rounded-2xl border-4"
            style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              padding: isAndroid && isMobile ? '0.75rem' : (isMobile ? '1rem' : '1.5rem'),
              maxHeight: isMobile ? 'calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 2rem)' : '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} md:text-3xl font-black mb-4`} style={{ color: '#3e2615' }}>
              {isPostAd ? "Continue to Match" : "Remove ads forever?"}
            </h2>
            
            <p className={`${isMobile ? 'text-sm mb-4' : 'text-base mb-6'}`} style={{ color: '#3e2615' }}>
              {isPostAd
                ? "Continue to the game or remove ads to skip future prompts."
                : "Purchase Remove Ads to enjoy an ad-free gaming experience."}
            </p>

            {error && (
              <p className={`${isMobile ? 'text-xs mb-4' : 'text-sm mb-4'}`} style={{ color: '#d32f2f' }}>
                {error}
              </p>
            )}

            <div className="flex flex-col gap-3">
              {isPostAd ? (
                <>
                  <button
                    onClick={onContinue || onClose}
                    disabled={loading}
                    className="w-full rounded-lg font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: '#fffaeb',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                      boxShadow: '0 4px 0 #8b6542',
                      padding: isMobile ? '0.75rem 1rem' : '0.75rem 1.5rem',
                      fontSize: isMobile ? '0.875rem' : '1.125rem'
                    }}
                  >
                    Continue to Match
                  </button>
                  <button
                    onClick={handleRemoveAds}
                    disabled={loading}
                    className="w-full rounded-lg font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: 'transparent',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                      padding: isMobile ? '0.75rem 1rem' : '0.75rem 1.5rem',
                      fontSize: isMobile ? '0.875rem' : '1.125rem'
                    }}
                  >
                    {loading ? 'Processing...' : 'Remove Ads'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleRemoveAds}
                    disabled={loading}
                    className="w-full rounded-lg font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: '#fffaeb',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                      boxShadow: '0 4px 0 #8b6542',
                      padding: isMobile ? '0.75rem 1rem' : '0.75rem 1.5rem',
                      fontSize: isMobile ? '0.875rem' : '1.125rem'
                    }}
                  >
                    {loading ? 'Processing...' : 'Remove ads'}
                  </button>
                  
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="w-full rounded-lg font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: 'transparent',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                      padding: isMobile ? '0.75rem 1rem' : '0.75rem 1.5rem',
                      fontSize: isMobile ? '0.875rem' : '1.125rem'
                    }}
                  >
                    Not now
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
